/**
 * How a config's `playgrounds` pattern names files: `gotti playground`'s walk over the disk (`node.ts`) and a host that
 * lists a project's files without one (`matchPlaygroundPaths`) apply the same rules, from here. Pure and browser-safe.
 */

/**
 * Convert a simple glob pattern to a regex.
 * Supports: * (any chars except /), ** (any chars including /), ? (single char)
 * Handles glob star followed by slash at the start to match zero or more directories (including root).
 */
export const playgroundPatternRegex = (pattern: string): RegExp => {
  const regexStr = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&') // Escape special regex chars (except * and ?)
    .replace(/\*\*\//g, '{{GLOBSTAR_SLASH}}') // **/ can match empty or any path with trailing /
    .replace(/\*\*/g, '{{GLOBSTAR}}')      // Temporarily replace **
    .replace(/\*/g, '[^/]*')               // * matches anything except /
    .replace(/\?/g, '.')                   // ? matches single char
    .replace(/{{GLOBSTAR_SLASH}}/g, '(?:.*/)?') // **/ matches "" or "anything/"
    .replace(/{{GLOBSTAR}}/g, '.*');       // ** matches anything including /
  return new RegExp(`^${regexStr}$`);
};

/** Whether a pattern (or one segment of it) is a glob: it holds `*` or `?`. */
export const isPlaygroundGlob = (pattern: string): boolean => /[*?]/.test(pattern);

/** A pattern as `gotti playground` reads it: a leading `./` dropped. */
export const normalizePlaygroundPattern = (pattern: string): string => (typeof pattern === 'string' ? pattern.replace(/^\.\//, '') : '');

/**
 * A glob split at its first glob segment: its literal leading folders (`playgrounds` in `playgrounds/*.playground.ts`),
 * the segments from there on, and whether any of those is a `**` — what a walk starts from, how deep it may go, and
 * whether it may go anywhere.
 */
export const splitPlaygroundPattern = (pattern: string): { prefix: string; segments: string[]; globstar: boolean } => {
  const all = pattern.split('/');
  const firstGlob = all.findIndex(isPlaygroundGlob);
  const segments = all.slice(firstGlob);
  return { prefix: all.slice(0, firstGlob).join('/'), segments, globstar: segments.some((segment) => segment.includes('**')) };
};

/**
 * The paths (relative to the config's folder, `/`-separated) a `playgrounds` pattern names, in the order given, by the
 * rules `gotti playground`'s walk applies: an explicit path — no `*` or `?` — names one file, there when it is among
 * `paths`; a glob names the paths under its literal leading folders whose rest it matches. The walk visits only
 * folders that could hold a match, so it finds exactly these.
 */
export const matchPlaygroundPaths = (pattern: string, paths: readonly string[]): string[] => {
  const normalized = normalizePlaygroundPattern(pattern);
  if (!normalized) return [];
  if (!isPlaygroundGlob(normalized)) return paths.includes(normalized) ? [normalized] : [];
  const { prefix, segments } = splitPlaygroundPattern(normalized);
  const regex = playgroundPatternRegex(segments.join('/'));
  return paths.filter((candidate) => {
    if (prefix && !candidate.startsWith(`${prefix}/`)) return false;
    return regex.test(prefix ? candidate.slice(prefix.length + 1) : candidate);
  });
};

/** A playground's name from its file (a path or a base name): the base name without its extension and a `.playground` suffix. */
export const playgroundNameOfFile = (file: string): string => {
  const base = file.slice(file.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  return (dot > 0 ? base.slice(0, dot) : base).replace(/\.playground$/, '');
};
